import { memo, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { getDottedQuestionName, getQuestionOptions, pathQuestionIdToOld } from '@aytm/app/stats/page/comments/lib/utils'
import style from '@aytm/app/stats/page/comments/image-responses/styles/ImageResponse.module.scss'
import moment from 'moment'
import { getImageAssetPath, IMAGE_ASSET_PATH_FILTER_SMART_CROP } from '@aytm/lib/common/assets'
import classNames from 'classnames'
import ImagePopup from '@aytm/lib/rfd-ui/components/basic/image-popup'
import { OEContext } from '@aytm/app/stats/page/comments'
import { DEFAULT_SELECTED_QUESTION } from '@aytm/app/stats/page/comments/lib/constants'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// Static same-origin placeholder: routing it through the asset-cache resolver made the
// fallback depend on an object in the media bucket, which is not there and answers 403.
const noImageSrc = '/images/external_survey/missing_image.png'

const ImageResponseItem = memo(
  ({
    dataId,
    responseId,
    path,
    question,
    survey,
    answer,
    response,
    isRejected,
    isShowPopup,
    setIsShowPopup,
    onUpdateParams
  }) => {
    const { searchState } = useContext(OEContext)

    const { request } = searchState
    const { selectedQuestion } = request

    const [isLoaded, setIsLoaded] = useState(false)
    const [isError, setIsError] = useState(false)

    const questionOptions = useMemo(() => getQuestionOptions(question, survey, false, true), [survey, question])

    const onResponseClick = () => {
      onUpdateParams({ query: [{ value: responseId, label: responseId, type: 'uid' }] }, undefined, 'comments')
    }

    const onLoadImage = useCallback(() => {
      setIsLoaded(true)
    }, [setIsLoaded])

    const onErrorImage = useCallback(
      ({ currentTarget }) => {
        setIsError(true)
        currentTarget.onerror = null
        currentTarget.src = noImageSrc
      },
      [setIsError]
    )

    const onImageDownload = useCallback(event => {
      event.stopPropagation()
    }, [])

    const renderQuestionWithAnswers = (node, path) => {
      const nodeData = node.data ? node.data : node
      const nodeOptions = questionOptions.find(item => item.value && item.value.replace(/sq(\d+)/i, 'b$1') == path)
      if (!nodeOptions || !nodeOptions.value) {
        return null
      }
      const itemPath = pathQuestionIdToOld(getDottedQuestionName(path))
      const src = getImageAssetPath({
        filter: IMAGE_ASSET_PATH_FILTER_SMART_CROP,
        path: nodeData.i.original.path,
        backgroundColor: 'none'
      })

      return (
        <div className={style['image-response']}>
          {!isError && (
            <a
              className={style['image-response-download']}
              aria-label="Download image"
              href={nodeData.i.original.url}
              download={`AYTM_Survey-${PageGlobals.SURVEY_ID}_Image-Response_${response.response.uid}_${itemPath}.jpg`}
              onClick={onImageDownload}
            >
              <i className="icon-aytmfd-export-v2 icon-shadow" />
            </a>
          )}
          {isRejected && response.rejection ? (
            <div className={style['response-rejected-info']}>
              by {response.rejection.rejected_by},&nbsp;
              {moment(new Date(response.rejection.rejected_at)).format('MM/DD/YYYY [at] hh:mm A')}
            </div>
          ) : null}
          <img
            alt={responseId}
            className={classNames({
              [style['image-response-image']]: true,
              [style['no-zoom']]: isError,
              [style['loading']]: !nodeData.i.original.preload_thumbnail && !isLoaded
            })}
            style={{
              backgroundImage:
                !isLoaded && nodeData.i.original.preload_thumbnail
                  ? `url(${nodeData.i.original.preload_thumbnail})`
                  : 'none',
              backgroundSize: 'cover'
            }}
            data-id={dataId}
            key={`${responseId}-question-item-${itemPath}-${nodeOptions.lvl}`}
            id={`response-image-${nodeData.i.uid}`}
            src={src}
            onError={onErrorImage}
            onClick={() => (!isError ? setIsShowPopup(`${responseId}_${path}`) : null)}
            onLoad={onLoadImage}
          />
          <ImagePopup
            src={nodeData.i.original.url}
            show={isShowPopup && isShowPopup == `${responseId}_${path}`}
            closeOnClick
            onClose={() => setIsShowPopup(null)}
          />

          <div className={style['image-response-description']}>
            <div className={style['image-response-description-inner']} onClick={onResponseClick}>
              {!selectedQuestion || (selectedQuestion && selectedQuestion == DEFAULT_SELECTED_QUESTION) ? (
                <span className={style['image-response-description-inner-path']}>{path}</span>
              ) : null}
              <span className={style['image-response-description-inner-open']}>Open response</span>
            </div>
            <div className={style['image-response-description-id']}>{responseId}</div>
          </div>
        </div>
      )
    }

    return renderQuestionWithAnswers(answer, path)
  }
)

ImageResponseItem.propTypes = {
  dataId: PropTypes.string,
  responseId: PropTypes.number,
  path: PropTypes.string,
  question: PropTypes.object,
  survey: PropTypes.object,
  isRejected: PropTypes.bool,
  response: PropTypes.object,
  answer: PropTypes.object,
  tags: PropTypes.array,
  isShowPopup: PropTypes.string,
  setIsShowPopup: PropTypes.func,
  onUpdateParams: PropTypes.func
}

ImageResponseItem.displayName = 'ImageResponseItem'

export default ImageResponseItem
